import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const dialogs = [];
const backgroundState = new Map();
let originalOverflow = '';
const restoreNode = (node, [inert, hidden]) => {
  node.inert = inert;
  if (hidden === null) node.removeAttribute('aria-hidden'); else node.setAttribute('aria-hidden', hidden);
};
const syncBackground = () => {
  if (!dialogs.length) {
    backgroundState.forEach((state, node) => restoreNode(node, state));
    backgroundState.clear();
    document.body.style.overflow = originalOverflow;
    return;
  }
  const top = dialogs[dialogs.length - 1];
  [...document.body.children].forEach(node => {
    if (!backgroundState.has(node)) backgroundState.set(node, [node.inert, node.getAttribute('aria-hidden')]);
    if (node === top) restoreNode(node, backgroundState.get(node));
    else { node.inert = true; node.setAttribute('aria-hidden', 'true'); }
  });
  document.body.style.overflow = 'hidden';
};
const focusable = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// A root portal keeps named layers comparable. Background inertness also
// prevents screen readers and keyboard users from reaching covered controls.
const Dialog = ({ children, onClose, label, labelledBy, describedBy, initialFocusRef, className, dismissOnBackdrop = false }) => {
  const [host] = useState(() => document.createElement('div'));
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previousFocus = document.activeElement;
    if (!dialogs.length) originalOverflow = document.body.style.overflow;
    host.dataset.taptapDialog = 'true';
    document.body.appendChild(host);
    dialogs.push(host);
    syncBackground();
    // preventScroll: without it, focusing an element near the bottom of a
    // scrollable dialog (e.g. the only focusable control being a trailing
    // Close button) scrolls the whole dialog to the bottom on open.
    const focusFirst = () => (initialFocusRef?.current || panelRef.current?.querySelector(focusable) || panelRef.current)?.focus({ preventScroll: true });
    focusFirst();
    // WebKit has a long-standing history of not reliably honoring focus()'s
    // preventScroll option, so don't rely on it alone — directly reset any
    // scrollable container's position after the initial focus.
    panelRef.current?.querySelectorAll('*').forEach(el => {
      if (el.scrollHeight > el.clientHeight) el.scrollTop = 0;
    });
    const onKey = event => {
      if (dialogs[dialogs.length - 1] !== host) return;
      if (event.key === 'Escape' && closeRef.current) {
        event.preventDefault(); event.stopPropagation(); closeRef.current();
      }
      if (event.key !== 'Tab') return;
      const controls = [...(panelRef.current?.querySelectorAll(focusable) || [])];
      if (!controls.length) { event.preventDefault(); panelRef.current?.focus(); return; }
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    const onFocus = event => {
      if (dialogs[dialogs.length - 1] === host && !host.contains(event.target)) focusFirst();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocus);
      dialogs.splice(dialogs.indexOf(host), 1);
      host.remove();
      syncBackground();
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [host, initialFocusRef]);

  return createPortal(
    <div ref={panelRef} role="dialog" aria-modal="true" aria-label={label} aria-labelledby={labelledBy}
      aria-describedby={describedBy} tabIndex={-1} className={className}
      onMouseDown={event => { if (dismissOnBackdrop && event.target === event.currentTarget) onClose?.(); }}>
      {children}
    </div>, host
  );
};
export default Dialog;
